import { c as _c } from "react/compiler-runtime";
import { GroupsDemoEmptyPage } from "@components/common/empty-page/groups-demo-empty-page";
import { GroupsEmptyPage } from "@components/common/empty-page/groups-empty-page";
import { PageContainer } from "@components/common/page-container";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { DEFAULT_GROUPS_SORT_BY } from "@components/groups/groups-constants";
import { GroupsSort } from "@components/groups/groups-sort";
import { useGroupsFilters } from "@components/groups/groups-filters/use-groups-filters";
import { BulkExportGroups } from "@components/table/groups/bulk-actions/bulk-export-groups";
import { isGroupsSortBy } from "@custom-types/type-guards";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useGroups } from "@hooks/use-groups";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { GroupsActionButtons } from "@pages/groups/groups-action-buttons";
import { CreateGroup } from "@components/groups/create-group";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useMediaQueryList } from "@hooks/use-media-query";
import { GroupsMobileView } from "@pages/groups/groups-mobile-view";
import { fetchingGroupsFlagsSelector, filteredCompanyGroupsSelector } from "@store/groups/groups-selector";
import { useAppSelector } from "@store/store-helper";
import { dataViewModeSelector } from "@store/ui/ui-selector";
import { isGroupDemo, sortGroups } from "@utils/group-utils";
import { useMemo } from "react";
import { GroupsPageTable } from "@pages/groups/groups-page-table";
import { GroupsTiles } from "@pages/groups/groups-tiles";

/** Content in the Groups page */
export function GroupsPage() {
  const $ = _c(51);
  const companyId = useCompanyId();
  const groups = useGroups();
  const filteredCompanyGroups = useAppSelector(filteredCompanyGroupsSelector);
  const {
    groups: viewMode
  } = useAppSelector(dataViewModeSelector);
  const {
    isFetchingGroups
  } = useAppSelector(fetchingGroupsFlagsSelector);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      defaultSort: DEFAULT_GROUPS_SORT_BY,
      isValidSort: isGroupsSortBy
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    sortBy,
    setSortBy
  } = useSubjectsSortByQuery(t0);
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const {
    canCreateGroups
  } = useHasUserValidRoleCompanyLevel();
  const {
    isAnyFilterApplied,
    filterGroups,
    filterChips
  } = useGroupsFilters();
  let t1;
  if ($[1] !== filterGroups || $[2] !== filteredCompanyGroups || $[3] !== sortBy) {
    t1 = sortGroups(filterGroups(filteredCompanyGroups), sortBy);
    $[1] = filterGroups;
    $[2] = filteredCompanyGroups;
    $[3] = sortBy;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const sortedGroups = t1;
  if (groups.length === 1 && isGroupDemo(groups[0]) && sortedGroups.length === 1) {
    let t2;
    if ($[5] !== companyId || $[6] !== sortedGroups[0]) {
      t2 = <GroupsDemoEmptyPage companyId={companyId} demoGroup={sortedGroups[0]} />;
      $[5] = companyId;
      $[6] = sortedGroups[0];
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  const shouldShowEmptyPage = sortedGroups.length === 0 && !isFetchingGroups;
  let t2;
  if ($[8] !== companyId || $[9] !== isFetchingGroups || $[10] !== setSortBy || $[11] !== sortBy || $[12] !== sortedGroups) {
    t2 = <GroupsPageTable companyId={companyId} groups={sortedGroups} isLoading={isFetchingGroups} sortBy={sortBy} setSortBy={setSortBy} />;
    $[8] = companyId;
    $[9] = isFetchingGroups;
    $[10] = setSortBy;
    $[11] = sortBy;
    $[12] = sortedGroups;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let groupsContent = t2;
  if (viewMode === "cards" && !isScreenSmOrSmaller) {
    let t3;
    if ($[14] !== sortedGroups) {
      t3 = <GroupsTiles groups={sortedGroups} />;
      $[14] = sortedGroups;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    groupsContent = t3;
  }
  const shouldRenderActions = sortedGroups.length > 0;
  let t3;
  if ($[16] !== groups || $[17] !== isScreenSmOrSmaller) {
    t3 = isScreenSmOrSmaller ? undefined : <GroupsActionButtons groups={groups} />;
    $[16] = groups;
    $[17] = isScreenSmOrSmaller;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  const t4 = groups.length > 0 ? filterChips : undefined;
  let t5;
  if ($[19] !== canCreateGroups || $[20] !== groups || $[21] !== shouldShowEmptyPage) {
    t5 = !shouldShowEmptyPage && canCreateGroups && <CreateGroup groups={groups} />;
    $[19] = canCreateGroups;
    $[20] = groups;
    $[21] = shouldShowEmptyPage;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== canAssignManagers || $[24] !== setSortBy || $[25] !== shouldRenderActions || $[26] !== sortBy) {
    t6 = shouldRenderActions && <GroupsSort key="groups-sort" sortBy={sortBy} setSortBy={setSortBy} hiddenFields={canAssignManagers ? undefined : ["members"]} />;
    $[23] = canAssignManagers;
    $[24] = setSortBy;
    $[25] = shouldRenderActions;
    $[26] = sortBy;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== groups || $[29] !== shouldRenderActions) {
    t7 = shouldRenderActions && <BulkExportGroups key="BulkExportGroups" selectedGroups={groups} areEntitiesSelected={false} buttonType="standard" />;
    $[28] = groups;
    $[29] = shouldRenderActions;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== isAnyFilterApplied || $[32] !== isFetchingGroups || $[33] !== sortedGroups.length || $[34] !== t4 || $[35] !== t5 || $[36] !== t6 || $[37] !== t7) {
    t8 = <EntitiesToolbar toolbarEntityName="Group" numberOfRows={sortedGroups.length} isLoading={isFetchingGroups} filterChips={t4} isAnyFilterApplied={isAnyFilterApplied} mobileAction={t5} sortActions={t6} exportComponent={t7} />;
    $[31] = isAnyFilterApplied;
    $[32] = isFetchingGroups;
    $[33] = sortedGroups.length;
    $[34] = t4;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] !== groupsContent || $[40] !== isAnyFilterApplied || $[41] !== isFetchingGroups || $[42] !== isScreenSmOrSmaller || $[43] !== shouldShowEmptyPage || $[44] !== sortBy || $[45] !== sortedGroups) {
    t9 = shouldShowEmptyPage ? <GroupsEmptyPage isFiltering={isAnyFilterApplied} /> : <>{isScreenSmOrSmaller && <GroupsMobileView groups={sortedGroups} sortBy={sortBy} isLoading={isFetchingGroups} />}{!isScreenSmOrSmaller && groupsContent}</>;
    $[39] = groupsContent;
    $[40] = isAnyFilterApplied;
    $[41] = isFetchingGroups;
    $[42] = isScreenSmOrSmaller;
    $[43] = shouldShowEmptyPage;
    $[44] = sortBy;
    $[45] = sortedGroups;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  let t10;
  if ($[47] !== t3 || $[48] !== t8 || $[49] !== t9) {
    t10 = <PageContainer title="Groups Management" dataTestId="GROUPS_CONTAINER" actionButtons={t3}>{t8}{t9}</PageContainer>;
    $[47] = t3;
    $[48] = t8;
    $[49] = t9;
    $[50] = t10;
  } else {
    t10 = $[50];
  }
  return t10;
}